"use client";

import { createContext, useContext, type ReactNode } from "react";
import { CanvasClickTextEditor } from "@/components/landing/canvas-text-popover";
import {
  landingTextHasInlineMarkup,
  styledTextString,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingCanvasEditApi = {
  enabled: boolean;
  patch: (path: string, value: LandingTextValue) => void;
};

export const LandingCanvasEditContext =
  createContext<LandingCanvasEditApi | null>(null);

export function useLandingCanvasEdit() {
  return useContext(LandingCanvasEditContext);
}

/** На публичном сайте — просто текст. В редакторе (внутри Provider) — клик → попап. */
export function LandingCanvasText({
  field,
  value,
  label,
  multiline,
  children,
  hideIfEmpty,
  inline,
  alignFallback,
}: {
  field: string;
  value: LandingTextValue;
  label: string;
  multiline?: boolean;
  children: ReactNode;
  hideIfEmpty?: boolean;
  inline?: boolean;
  /** Выравнивание обёртки, если в value нет align (например center у NewsHero). */
  alignFallback?: "left" | "center" | "right";
}) {
  const edit = useLandingCanvasEdit();
  const empty =
    !styledTextString(value) && !landingTextHasInlineMarkup(value);

  // hideIfEmpty — и на сайте, и в редакторе (иначе «Вторая кнопка» / «Лид» ломают вёрстку).
  if (hideIfEmpty && empty) return null;

  if (!edit?.enabled) {
    return <>{children}</>;
  }

  return (
    <CanvasClickTextEditor
      value={value}
      onChange={(next) => edit.patch(field, next)}
      label={label}
      multiline={multiline}
      inline={inline}
      alignFallback={alignFallback}
    >
      {children}
    </CanvasClickTextEditor>
  );
}
